import React, { useState, useEffect, useRef } from "react";
import { getBaseUrl } from '../../Utils';
interface PageContent {
  id: string;
  title: string;
  content: string;
  imagePath?: string;
  videoPath?: string;
  isVideo?: boolean;
  backupImagePath?: string;
}
interface SectionData {
  pages: PageContent[];
}
interface IntroductionProps {
  isOpen: boolean;
  onClose: () => void;
  contentData?: Record<string, SectionData>;
}

const FoundationsGuide: React.FC<IntroductionProps> = ({ isOpen, onClose, contentData }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [activeSection, setActiveSection] = useState('Introduction');
  const [preloadedImages, setPreloadedImages] = useState<Record<string, string>>({});
  const [preloadedVideos, setPreloadedVideos] = useState<Record<string, string>>({});
  const [videoLoadingStates, setVideoLoadingStates] = useState<Record<string, boolean>>({});
  
  // Store preloaded images
  const imageCache = useRef<Record<string, HTMLImageElement>>({});

  // Default content data - can be overridden by props
  const defaultContentData: Record<string, SectionData> = {
    'Introduction': {
      pages: [
        {
          title: "What is Foundations™",
          id: "tutorial_section_image_1_1",
          content: `Foundations™ from HealthLevel is a real-time analytics platform that integrates clinical, financial, and operational data from connected systems such as EMR, inventory & billing, lab, department-specific applications, etc. By aggregating data from different systems, our users can quickly answer questions that impact their role. Below are a few examples of how Foundations™ can make an impact within your role: <br /><br />
          1) Administrators and department management can quickly measure historical and real-time trends to track the impact of their decisions. For example: patients with 30-day readmissions, room utilization by the month, exam delays, wait time trends, etc. <br />
          2) Clinicians can measure their performance based on clinical outcomes of their patients. For example: my number of cases with complications, my cases with radiation dose 5Gy, etc. <br />
          3) Staff can measure their performance based on operational and clinical factors. For example: number of exams completed, Number of exams with missing documentation, etc. <br /><br />
          Foundations™ adapts and displays information based on user role, therefore making each users' experience very personalized. No more "Access denied" or "Please contact your administrator". Users with appropriate privileges will be able to create and share ad-hoc reports, drill into details of each data set/report, define benchmarks and organizational targets, and share reports and metrics with other users. <br /><br />
          Of course, the data available to you will vary based on the type and number of systems that are integrated into the Foundations™ platform. So let's get started with the tutorial so that you can begin discovering new insights! <br /><br />`,
          imagePath: `${getBaseUrl()}/app/tutorial/Foundations_Intro.png?v=2024-06-12`,
        },
        {
          title: "Where does data come from?",
          id: "tutorial_section_image_1_2",
          content: `Foundations™ gets its analytics from a variety of connected systems and associated feeds that include (but are not limited to) PACS, RIS, RCH, HL7, and DICOM. <br /><br />
          For example: <br />
          Orders and Results Information: Sourced from HL7 messages. <br />
          Image Metadata, Radiation Exposure, etc. – Sourced from DICOM feeds. <br />
          Financial: Sourced from HL7, EDI messages, and data files.`,
          imagePath: `${getBaseUrl()}/app/tutorial/Data-sources.png?v=2024-06-12`,
        }
      ]
    },
    'Homescreen': {
      pages: [
        {
          title: "Homescreen",
          id: "tutorial_section_image_2_1",
          content: `On successful sign in, the homepage serves as the landing page for all users, providing an overview of all the available workspaces.<br /><br />
                   Following are a few additional features that you can see on the home page - <br /><br />
                   <b>Workspace</b> - It is a sharable collection of various insights like summaries, forms, graphs, and other useful analytics in the form of reports. Workspaces can be Role based or can be shared with various users. You will get more details about Workspaces in a separate section. <br /><br />
                   <b>User Profile</b> - The drop-down menu under the username offers additional features such as display mode, settings, etc. You can get more details about these features in a separate section. <br /><br />
                   <b>Inbox</b> - For an administrator this serves as Foundations™ wide notification system. E.g. If a new user registers or a workspace is added/shared, the administrator will be notified through the Inbox. <br /><br />
                   <b>Show Help</b> – By choosing 'Show Help', users can easily access the tutorial to get more help on the features.`,
          imagePath: `${getBaseUrl()}/app/tutorial/Foundations_HomeScreen.png?v=2024-07-09`,
        }
      ]
    },
        'Workspaces': {
      pages: [
        {
          title: "Workspaces Overview",
          id: "tutorial_section_image_3_1",
          content: `After a successful sign in, you will be able to see the set of all workspaces assigned to you or created by you. A workspace is a sharable collection of various insights like summaries, forms, graphs, and other useful analytics in the form of reports. Workspaces can be Role based or can be shared amongst various users.<br><br> 

					<h3>Workspace Actions</h3> 

					Click on workspace and you will see all the reports in the workspace. There is the "Workspace Actions" menu on the top right corner of the screen.<br><br>
					
				<b> 1. Add New Workspace:</b> Users with specific privileges can create a new workspace. Provide the name and description of the workspace and set the access level. Click on "Add Workspace".<br><br>					
				<b>	2. Edit This Workspace:</b> Users with specific privileges can edit the name, description, and access level of the workspace by selecting this option. <br><br>					
				<b>	3. Refresh This Workspace:</b> Refreshes current workspace page.<br><br>					
				<b>	4. Expand First 10 reports:</b> Click on "Expand First 10 reports" to view the first 10 non-summary reports in the workspace in the expanded form.<br><br>					
				<b>	5. Collapse all reports:</b> Click on "Collapse all reports" to view all the non-summary reports in the workspace in the collapsed form.<br><br>					
				<b>	6. Delete this workspace:</b> Users with specific privileges can delete the workspace permanently. This operation cannot be undone.<br><br>					
				<b>	5. Detach this workspace:</b> Users with specific privileges can detach the workspace from their user account. This operation will not actually delete the workspace, rather it will just release it from this workspace.<br><br>  
				<b>	6. Add New Report:</b> Users with specific privileges can add a new report in the workspace.<br><br>					
				<b>	7. Attach Existing Report:</b> Users with specific privileges can search the report and attach it to their workspace.  <br><br>
				<b>	8. Copy from an existing report:</b> Users with specific privileges can copy the existing report in their own workspace.  <br><br>					
				<b>	9. Reorder reports:</b> The reports in the workspace can be reordered according to the user's preference.<br><br>		
				<b>	10. Email Workspace as PDF:</b> Workspace containing all the reports can be emailed to the registered email address in PDF format. `,
          imagePath: `${getBaseUrl()}/app/tutorial/WorkspaceActions.png?v=2024-07-09`,
        },
        {
            title: "Where does data come from?",
            id: "tutorial_section_image_3_2",
            content: `1. Users with specific privileges can share the workspace with other users. <br><br>	
					2. To share the workspace, click on “Share This Workspace” in workspace actions , search and select the user(s) you want to share the workspace with and click on "Save" to share the workspace with selected users.<br><br>
					Sharing Workspace through roles: <br><br><br>
					The administrators can also assign the workspaces to a specific role. All the users with that role can access the same workspace.<br><br>  
					To assign a workspace to a specific role, <br>
					1. Click on User Profile<br> 
					2. Select Admin Task<br> 
					3. Select Manage Role <br><br>					
					By selecting a role from the "role" dropdown menu, users can assign a workspace to that role. <br>
					Any user assigned to that role will automatically have the workspace added to their account.<br><br> 		
					Workspace access levels –  <br><br>

					
				<b>	1. Open, Changes Allowed :</b> The workspace with this access level when shared with other users can be edited, deleted, detached or shared by the other user. Other users can also add a new report, attach or copy an existing report in this workspace.<br><br>					
				<b>	2. Shared Lock, Restricted Updates :</b> The workspace with this access level when shared with other users can be edited or shared by the other user but can not be deleted or detached. Also, other users cannot add any new report, attach or copy any other report in this workspace.<br><br> 					
				<b>	3. Locked, only for me and administrators:</b> No users can edit, delete, detach and share this workspace, and no one can create any new report, attach or copy existing reports in the workspace.`,
            imagePath: `${getBaseUrl()}/app/tutorial/Foundations_SharingWorkspace.png?v=2024-07-29-1`,
          }
      ]
    },
    'Reports': { 
      pages: [
        {
          title: "Reports",
          id: "tutorial_section_image_4_1",
          content: `Reports within Foundations™ allow users to visualize the data based on selected measures, filters and organizers. Foundations™ reports are dynamic, and therefore users can dig into more details regarding a specific data point using the drill thru feature (will know more about this feature later in the tutorial).<br><br>
				There are two types of reports in Foundations™: Summary reports and Detailed reports.<br><br>
				<b>Summary Reports</b> <br><br>
				Think of summary reports as “reading the headlines of a newspaper”. Summary reports allow users to quickly gauge the progress of various measures and initiatives.<br><br> 
				For example, a summary report is used to quickly understand ER exam volumes and also monitor current wait times compared to the organizational target of 15 minutes. <br><br>
				
				Based on the type of data available within Foundations™, summary reports are not limited to monitoring volumes, but can also serve as an ‘at-a-glance’ report for monitoring or tracking critical data.  <br><br> 
				Note: Not all workspaces will include summary reports. Since Foundations™ provides user driven reporting, the type and availability of summary reports is dependent on the administrator that created the workspace. <br><br>
				<b>Detailed Reports</b><br><br>
				Now that you’ve learned about summary reports, let’s discuss the next type of report: Detailed reports.<br><br> 
				Detailed reports provide users with a more interactive interface. Here users can view trends and use visual and interactive discovery to understand their metrics. <br><br> 

				Because each user interprets information differently, Foundations™ allows users to view each report in multiple different formats such as Bar Chart, Pie chart,Line and column chart, Scatter(might not be present everytime) and Table.<br><br> 
				
				In addition, users with the appropriate security privileges may be able to create and modify reports to answer additional questions and share them with other users. This type of user security (Power Users) is often available for department managers, IT administrators and selected staff (management selected). <br><br>


				<b>Report Actions</b><br><br>
				<b>Add New Report</b> – Users with specific privileges can add new reports to the workspace. Summary reports can be created by selecting measures and filters. Table or Detailed reports can be created by selecting measures, filters and organizers. Report name, description, auto-refresh settings, drill thru settings, Access level and Auto-Next setting can be set while creating a new report.<br><br>
				<b>Edit</b> – Users with specific privileges can edit the report. The measures, filters and organizers can be updated.<br><br>
				<b>Change Report Configuration</b> – Report name, description, auto-refresh, drill thru settings, Access level and Auto-Next setting can be updated.<br><br>
				<b>Delete</b> - Users with specific privileges can delete the report.<br><br>
				<b>Detach</b> - Users with specific privileges can detach the report from their workspace.<br><br> `,
          imagePath: `${getBaseUrl()}/app/tutorial/Reports2.png?v=2024-07-09`,
        },
        {
            title: "Charts",
            id: "tutorial_section_image_4_2",
            content: `Chart types and layout are determined based on the tabulated data available from the configuration.<br>
				These charts are designed to be user-friendly, making complex data easier to understand. They are also organized into different types, each based on their unique settings, to provide a structured approach to data visualization.<br><br>
				<b>1. Pie chart:</b><br> 
					1.	A pie chart is a type of graph that represents the data in the circular graph.<br>
					2.	The slices of pie show the relative size of the data, offer a visual way to understand the information and provide an option to drill thru each segment to gain insight.<br>
					3.	A report can display up to three pie charts, either by incorporating three separate measures or by using one measure along with two cascading reports.<br><br>
				<b>2. Bar chart:</b><br>
					1.	A bar chart is a type of graph that uses rectangular bars to represent different categories of data, with the length or height of each bar being proportional to the value of the category it represents.<br>
					2.	A bar chart is used when you want to show a distribution of data points or perform a comparison of metric values across different subgroups of your data.<br>
					3.	An option is provided in bars to drill thru each segment to gain insight.<br><br>
				<b>3.  Line chart:</b><br>
					1.	A line chart connects a series of data points using a line.<br> 
					2.	This chart type presents sequential values to help users identify trends.<br> 
					3.	Most of the time, the x-axis (horizontal axis) represents a sequential progression of values.<br>
					4.	An option is provided on line to drill thru each segment to gain insight.<br><br> 
				<b>4. Column chart: </b><br>
					1.	A column chart is a vertical bar chart.<br>
					2.	It requires two values (X and Y) where X can be date/time, log, string, numeric, etc. On the other hand, the value on the Y axis is only numeric. <br><br> 
				<b>5. Scatter chart:</b><br>
					1.	A scatter chart, also called a scatter plot, is a chart that shows the relationship between two variables.<br>
					2.	They allow viewers to immediately understand a relationship or trend.<br>
					3.	A Scatter chart only works with 2 measures and can have only 1 organizer row or column. <br>
					4.	It shows the correlation between the 2 measures. <br><br>`,
            imagePath: `${getBaseUrl()}/app/tutorial/Charts.png?v=2024-07-30`,
          },
          {
            title: "DrillThru",
            id: "tutorial_section_image_4_3",
            content: `Foundations™ provides the drill thru capability for reports, which allows users to dig into more details regarding a measure value. Users with specific privileges can launch the drill thru for a specific data point.<br><br>

					<b>Launch Drill Thru for Summary Report</b><br>
					1.	To launch the drill thru option – Allow drill thru setting should always be set to "Yes"
					(Please refer to Add a Report show help section for more details).<br>

					2.	The drill thru can be launched on click of a data point in the table. <br><br>

					<b>Launch drill thru for non-summary report</b><br>
					To launch the drill-through feature, click on the data point on the chart diagram.<br><br>


					<b>Features of Drill Thru</b><br><br>

					<b>1. Switch Drill thru set &amp; Make default :</b><br>
					1. Users with the appropriate privileges can see a list of available drill thru sets.<br>

					2. Switch to another drill thru set option and click <b>“Make Default”</b> button.<br>

					3. This helps users to make the drill thru set as the default view.<br><br>

					<b>2. Set View :</b> This helps users to rearrange the columns in the drill thru set as required and set the view for that drill thru set.<br><br>
					<b>3. Search :</b>	A column-based search is provided for the drill thru. This helps the user search for a keyword in the specific column and filter the records.<br><br>
					<b>4. Export :</b>	The drill thru data can be exported in the csv format on the click of the <b>“Export”</b> button.<br><br>
					<b>5. Refresh :</b> Manually refresh the drill thru with latest update.<br><br>
					<b>6. Close :</b> Close the drill thru.<br><br>`,
            imagePath: `${getBaseUrl()}/app/tutorial/DrillThru.png?v=2024-07-28`,
          },
          {
            title: "DrillThru Sort",
            id: "tutorial_section_image_4_4",
            content: `	<b>How to apply drill thru sort by using a predefined set of the columns.</b><br><br> 

						1. Users with appropriate privileges can apply sort on the drill thru set using a predefined list of the columns either in ascending or descending order.<br><br> 
						2. <b>"Sort by"</b> widget which includes a dropdown list of predefined columns that can be used for sorting. There will be a separate "Apply Sort" button. The sort will be applied on click of this button.<br><br> 
						3. The drillthru/worklist will show indicators (e.g., arrows) next to the sorted column to indicate the sort order.<br><br> 
						4. Sorting can be used by all the users who can view that drill thru. The sort will be saved for that default view when clicked on “Make Default” button. The users having access to the “Make Default” feature can sort the columns.<br><br> 
						5. In case of multiple drillthru sets in the dropdown, sorting will be saved for only one drillthru set at a time.<br> `,
            imagePath: `${getBaseUrl()}/app/tutorial/DrillThruSort.png?v=2024-07-28`,
          },
          {
            title: "Manipulating Summary Reports",
            id: "tutorial_section_image_4_5",
            content: `With a summary report, there are additional features that the user can use to keep track of one's progress. <br><br>
							<b>Set Target :</b> 
							By setting targets on the measures within a summary report, users can effectively track the progress toward the business goals. This enhances visibility and helps stakeholders to make informed decisions based on the progress of the measures as per the set target.<br><br> 
							There are two types of targets:<br> 
							1. Personalized target <br>
							2. Global preset targets <br><br>	
							<b>Personalized target :</b> is set on a user level, where one can set a target to align with the business outcome and enables them to take timely actions or make informed decisions.<br><br> 	
							<b>Set up a personalized target for the report</b><br><br> 	
							1. Click on the <b>"Add Target"</b> option to set up personalized target.<br> 	
							2. The <b>"Assign Target"</b> pop up will be displayed, allowing the user to set a personalized target by providing required inputs.<br> 	
							3. Please make sure the <b>“Update Preset”</b> checkbox is unchecked if it is available.<br>	
							4. Please select the user target (User Defined / Business Parameter), select the target type (Aim to exceed/ Stay Under), enter the value, and then click on <b>“Set Personal Target”</b> button.<br><br> 
							<b>Edit personalized target</b><br> 
							1. Click on the "Edit Target" option to edit the personalized target.<br> 
							2. The "Assign Target" pop up will be displayed, allowing the user to edit a personalized target.<br> 
							3. User can edit the required field target (User Defined / Business Parameter), target type (Aim to exceed/Stay Under), the value, and then click on “Set Personal Target” button.<br><br> 
							<b>Delete personalized target</b><br> 
							1. For a set target, the user just needs to click on<b> “Delete Personal Target”</b>  button and personalized target will get deleted.`,
            imagePath: `${getBaseUrl()}/app/tutorial/Foundations_ManipulatingSummary.png?v=2024-07-09`,
          },
          {
            title: "Global preset measure target",
            id: "tutorial_section_image_4_6",
            content: `Global Preset measure targets allow privileged users (Admins) to set up common targets for measures on any summary report. The reports once shared with other users, they can view the same targets set by the admin. If users have specific requirements, they can choose not to use the global preset target and set their own personalized targets later.<br><br>
						<b>How to set up a global preset measure target</b> <br><br>						
						1. Click on the <b>"Add Target"</b> option to set up the Global preset measure target. <br>						
						2. The <b>“Assign target”</b> pop up will be displayed.<br>						
						3. Please select the user target (User Defined / Business Parmeter), select the target type (Aim to exceed, Stay Under), enter value and check the <b>“Update Preset”</b> check box and then click on <b>“Set Global Target” </b>button.<br>		
						4. Once the target is set, the same target will be reflected to other users with whom the report is shared. <br><br>
						<b>How to use global preset measure target </b><br><br>
						1. Once the admin sets the global preset measure target for a report, the users with whom the report is shared will get inbox notification for same. <br>					
						2. Users with whom the report is shared can view the global preset measure target set by the admin for them.<br>						
						3. Users can use the global preset measure target by clicking on the <b>"Use Preset"</b> checkbox. <br>						
						4. The set global preset measure target will be visible for that report.<br><br> 
						<b>Edit global preset measure target</b><br><br>
						1. Click on “Edit Target” option to edit the global preset measure target. <br>						
						2. Uncheck <b>“Use Preset”</b> checkbox, check on <b>“Update preset”</b>, then update the preset measure target (target type, value etc.) as required, and then click on <b>“Set Global Target”</b> button.<br>						
						3. Global preset measure target will be updated. <br>						
						4. The gauge will be reflected based on the updated measure target.<br><br> 
						<b>Delete global preset measure target </b><br><br>
						1. Click on <b>“Edit Target”</b> option to edit the global preset measure target.<br>						
						2. Uncheck the <b>“Use preset”</b> checkbox, click on the <b>"Update Preset"</b> and then click <b>"Delete global Target"</b>.<br>						
						3. Global preset measure target will be deleted. <br>						
						4. The gauge will not display once the target is deleted.<br><br>`,
            imagePath: `${getBaseUrl()}/app/tutorial/GlobalPreset.png?v=2024-07-09`,
          },
          {
            title: "KMI (Keep me Informed)",
            id: "tutorial_section_image_4_7",
            content: `Once the targets are set up for summary report, KMI (Keep Me Informed) notifications can also be set up for same. (Please refer to Help on how to set up the target.) KMI (Keep Me Informed) email notifications help users stay informed about business outcomes and enable them to take timely actions or make informed decisions to stay on track. KMI (Keep Me Informed) email notifications can be set up for below target types- <br><br>
							
							<b>Aim to exceed</b>- If the current value is less than the configured target value then emails will be sent.<br><br>							
							<b>Stay Under</b> - if the current value is greater than or equal to the configured target value then the emails will be sent. <br><br>

							1. Select the target type (Aim to exceed, and stay under), enter the value. Click on “Keep Me Informed” checkbox to set up the KMI notification for the set target.<br> 
							
							2. Only one measure target can be set at a time for the summary report (Aim to exceed or  stay under) and KMI notification can be set up for only one set target. <br><br>
							<b>KMI (Keep me Informed) <br>

							Auto Refresh settings and KMI email notifications</b><br><br> 
							
							<b>CASE 1 :</b> The Auto-Refresh is set as <b>“No”</b> for the summary report and the KMI checkbox is checked.							
							1. The <b>“Email Once per day”</b> checkbox on the “Edit Target” page will not be visible to the user as the Auto-Refresh is set to “No”.<br>							
							2. KMI notification email will be sent once per day.<br><br> 							
							<b>CASE 2 :</b> <br>							
							1. Auto-Refresh is set to <b>“Yes”</b> for the summary report. The <b>“Email Once per day”</b> checkbox is visible on the <b>“Edit Target”</b> page.<br><br>							
							2. Auto-Refresh time Interval Every 1 / 5 /10 minute - Email will be sent every 10 minutes if <b>“Email Once per day”</b> is unchecked.<br><br>							
							3. Auto-Refresh time interval Every 15 / 30 / 45/ 60 minute - Email will be sent as per the selected time interval. if <b>“Email Once per day”</b> is unchecked.<br><br>							
							4. If <b>“Email Once per day”</b> is checked, then regardless of the set auto-refresh time interval, the user will receive the email once per day. <br><br>`,
            imagePath: `${getBaseUrl()}/app/tutorial/Foundations_KMI.png?v=2024-07-28`,
          },
          {
            title: "KMI (Keep me Informed)",
            id: "tutorial_section_image_4_8",
            content: `<b>Example 1 : Aim to Exceed.</b><br> 
					The example demonstrates how to set up the measures target to aim to exceed a target value and get a KMI notification based on the Auto refresh configuration.<br> 
					A summary report is created with the following configuration. <br> <br> 
					
					Name of the report : Audit Counts.<br> 
					Auto-Refresh : Set to every 10 minutes.<br> 
					The measure target is set to Aim to exceed with the target value of 650000 and “Keep Me Informed” is checked.<br><br> 
					
					<b> Grey :</b> It indicates the completed part of the target value.<br> 					
					 <b>Red :</b> It shows the difference between the current measure value and the set target measure value.<br> 					
					 <b>Green :</b> Once the current measure value exceeds the set measure target value, it is represented by the green color.<br>					
					 <b>KMI Notification :</b> An email will be sent to the registered user's email address every 10 minutes (or based on auto refresh setting) till the current value is less than the configured target value. <br><br>`,
            imagePath: `${getBaseUrl()}/app/tutorial/Foundations_KMIAimToExceed.png?v=2024-07-09`,
          },
          {
            title: "KMI (Keep me Informed)",
            id:"tutorial_section_image_4_9",
            content: `<b>Example 2 :Stay Under.</b><br> 
					The example demonstrates how to set up the measure target to track if the measure value is falling below the set measure target value and get KMI notification based on the Auto-Refresh configuration..<br> 
					A summary report is created with the following configuration.<br> <br> 
					
					Name of the report : Audit Counts.<br> 
					Auto-Refresh : Set to every 10 minutes.<br> 
					The measure target is set to check if the measure value is falling under the set target value of 350000 for Stay Under<br><br> 
					
					<b>Red</b> - It indicates the gap between the set target and the exceeded value.<br> 
					<b>Green</b> - The indicator's position suggests the current measure value is falling below the set target value.<br>					
					<b>KMI Notification</b> - An email will be sent to the registered user's email address every 10 minutes if it exceeds the set target value.<br><br> `,
            imagePath: `${getBaseUrl()}/app/tutorial/Foundations_KMIStayUnder.png?v=2024-07-09`,
          },
      ]
    },
    'User Management': {
      pages: [
        {
          title: "Privilege Levels",
          id: "tutorial_section_image_5_1",
          content: `<strong>Administrators</strong> may approve new users, change role settings, lock/unlock users, check error logs and data load health summaries. Your IT staff are typically your administrative users.<br><br>
				<strong>Power Users</strong> may create workspaces and reports. Basically, Power Users have access to all the data available. There should be few Power Users in your application environment, but we do not impose artificial restrictions.<br><br>
				<strong>User Accounts</strong> Most users in an organization fall into this category. User account holders may view workspaces, drill-through to raw fact level information, share workspaces, write notes. These users' data access may be restricted via attribute tags - e.g. the picture shows the User account for John Smith is associated with the user name 'John Smith'. To tag a user with an attribute value, simply scroll down the list of values and select the appropriate value.<br><br>`,
          imagePath: `${getBaseUrl()}/app/tutorial/Foundations_UserAdministration.png?v=2024-07-09`,
        },
        {
            title: "Roles",
            id: "tutorial_section_image_5_2",
            content: `Roles may be configured to simplify user administration. For example, you may have radiologist role, technologist role, customer site managers, etc.<br><br>
							 Workspaces may be assigned to roles and when a user belongs to a role, the set of workspaces in that role are available to the user.<br><br>
							 Roles (along with personalized workspaces) are particularly powerful in delivering self-service data analysis to your staff.
							 One workspace may belong to multiple roles and a user can belong to multiple roles, e.g. a user may belong to radiologist and management roles.<br><br>
							 Additionally, each role will have their own security codes. Security codes are certain privileges given to roles. Not every user will have all privileges, due to security reasons, but an admin can add or exclude specific 
							 privileges of a certain user.`,
            imagePath: `${getBaseUrl()}/app/tutorial/Foundations_KMIStayUnder.png?v=2024-07-09`,
      } ,
      {
        title: "Settings and User Profile",
        id: "tutorial_section_image_5_3",
        content: `When you open the settings page, you may view attributes associated with your user profile. Think of these as tags attached to your account.<br><br>
				  For example, a radiologist user may be tagged with a provider name, as would be a technologist user. Data available to users can be restricted by implementing profile attributes.<br><br>
				  When you are assigned a personalized workspace through your role, these attributes will be applied to restrict the visible data.`,
        imagePath: `${getBaseUrl()}/app/tutorial/Foundations_UserSettings.png?v=2024-07-09`,
      }
        
      ]
    },
    'Advanced Topics': {
      pages: [
        {
          title: "Configuring Reports",
          id: "tutorial_section_image_6_1",
          content: ` Users with appropriate security privileges may create new reports. Select "Add new report" from the workspace actions menu. Please think of a data analysis problem in your business language and use it to walk through a practice session.<br><br>

				1. Counting something is selecting measures. Measure is a standard unit used to express the size, amount, or degree of something.<br><br>

				2. Use filters to apply basic analysis and restrict the data set. Each filter condition progressively narrows the data set to a specific context.<br><br>

				3. You may choose to organize the results into rows and columns. This is optional. Organizers help in better data visualization.<br><br>

				The new report you wish to configure will appear in the same workspace, in line with other reports. As you configure a new report, you can refer to others for context.
`,
          imagePath: `${getBaseUrl()}/app/tutorial/Advanced1-ConfiguringVU.png?v=2015-07-28-1`,
        },
        {
          title: "Measures",
          id: "tutorial_section_image_6_2",
          content: ` Let's define "Measure". <br><br>
                  Think of a measuring tape, you would measure (or count the) length of a fabric. <br><br>
                  Two very basic measures are Audit count and Audit type. Other examples could be "Metadata id". There can be many different measures, and the exact set depends on the raw data available. <br><br>
                  Click on select a measure button to open the measures palette. The measure names are commonly terms used in the business with a meaningful description. <br><br>
                  When you select a measure, you may be able to select a mathematical function, e.g. Metadata id - total or average, minimum or maximum, it depends on the measure. <br><br>
`,
          imagePath: `${getBaseUrl()}/app/tutorial/Advanced2-Measures.png?v=2024-07-29-1`,
        },
        {
          title: "Filters",
          id: "tutorial_section_image_6_3",
          content: `Filters allow users to narrow down the data set so that they can answer focused questions around their needs. <br><br>
                  Let's discuss a simple example: A user wants to see all the audits of type "DrillThru view" created in may 2019. <br><br>
                  Since the user is only interested in data for specific types of audits, and a specific timeframe, he/she can add 2 filters: <br><br>
                  1st filter 'Audit type' to reduce the dataset only to type DrillThru view. <br><br>
                  2nd filter 'Audit date' to reduce the data and view audits created in May 2019.`,
          imagePath: `${getBaseUrl()}/app/tutorial/Advanced3-Filters.png?v=2016-07-14`,
        },
        {
          title: "Organization",
          id: "tutorial_section_image_6_4",
          content: `To organize the result set, simply open the row (or column) panel and view the attributes you may organize by.<br><br>
						We encourage simple organizations, however, you may specify up to 2 levels, for row and columns each.<br><br>
						When you select row-column organization, plan for optimal tabulated viewing. Do not allow charting to weigh on your choices. The system figures out the optimal chart layout based on attributes and cardinality.<br><br>
						On selecting the attribute for organization, you may then select a binning to group or cluster the data, e.g. date groups, patient age ranges, etc.<br><br>
`,
          imagePath: `${getBaseUrl()}/app/tutorial/Advanced4-Organization.png?v=2024-07-29-1`,
        },
        {
          title: "Personalized Reports",
          id: "tutorial_section_image_6_5",
          content: `Personalization allows multiple users to view their own set of data for a given report.<br><br>
				Empowering every member of your business is a powerful motivator to improve performance, staff satisfaction and management efficiency. We enable you to distribute personalized information in a scalable manner, easily and securely. Think of Personalized workspaces and reports as templates that derive the available information from the user account (i.e. user profile).<br><br>

				Personalized reports provide restricted information to the user. In our example, a non-comparative report would give David P his own audit records or to use another example, a non-comparative report would give a user their individual audit record.<br><br> 
 
				To create a non-comparative report, use the filter capability, and select the filter condition '-personalized via user setting-'. This filter condition will use the attribute value from the user profile to restrict the data set. `,
          imagePath: `${getBaseUrl()}/app/tutorial/Advanced5-Personalized1.png?v=2024-07-30`,
        },
        {
          title: "Personalized Reports",
          id: "tutorial_section_image_6_6",
          content: `To create personalized reports, the user account should be tagged with the appropriate attributes. As an example – The user account of David P may be tagged with the attribute “User Name” and value “David P”. The personalized audit report for user account David P will give a list of all audits for the user name “David P”.`,
          imagePath: `${getBaseUrl()}/app/tutorial/Advanced6-Personalized2.png?v=2024-07-30`,
        },
        {
          title: "Personalized Reports",
          id: "tutorial_section_image_6_7",
          content: `The screenshot shows the personalized audit report for David P.`,
          imagePath: `${getBaseUrl()}/app/tutorial/Advanced7-Personalized3.png?v=2024-07-30`,
        }
      ]
    },
    'Training Videos': {
      pages: [
        {
          title: "Video 1",
          id: "tutorial_section_image_7_1",
          content: `<strong>Video 1 Test Content for Video 1</strong>`,
          videoPath: `${getBaseUrl()}/app/tutorial/DemoVideo1.mp4`,
          isVideo: true
        },
        {
          title: "Video 2",
          id: "tutorial_section_image_7_2",
          content: `<strong>Video 2 New Content for Video 2 </strong>`,
          videoPath: `${getBaseUrl()}/app/tutorial/DemoVideo2.mp4`,
          isVideo: true
        }
      ]
    }
  };

  // Use provided content data or default
  const finalContentData = contentData || defaultContentData;
  
  // Get current section data
  const currentSectionData = finalContentData[activeSection];
  
  // Calculate total pages for current section
  const totalPages = currentSectionData?.pages?.length || 0;

  // Start loading an image
  const tryLoadImage = (imagePath: string, backupPath: string | undefined, key: string) => {
    // Set loading state immediately
    setLoadingStates(prev => ({
      ...prev,
      [key]: true
    }));

    const img = new Image();
    
    img.onload = () => {
      // Store in cache and update preloaded images
      imageCache.current[key] = img;
      setPreloadedImages(prev => ({
        ...prev,
        [key]: imagePath
      }));
      setLoadingStates(prev => ({
        ...prev,
        [key]: false
      }));
    };
    
    img.onerror = () => {
      if (backupPath) {
        const backupImg = new Image();
        
        backupImg.onload = () => {
          imageCache.current[key] = backupImg;
          setPreloadedImages(prev => ({
            ...prev,
            [key]: backupPath
          }));
          setLoadingStates(prev => ({
            ...prev,
            [key]: false
          }));
        };
        
        backupImg.onerror = () => {
          setLoadingStates(prev => ({
            ...prev,
            [key]: false
          }));
        };
        
        backupImg.src = backupPath;
      } else {
        setLoadingStates(prev => ({
          ...prev,
          [key]: false
        }));
      }
    };

   // Set timeout to prevent hanging on very slow images
    const timeoutId = setTimeout(() => {
      if (loadingStates[key]) {
        // If still loading after timeout, mark as not loading to allow navigation
        setLoadingStates(prev => ({
          ...prev,
          [key]: false
        }));
      }
    }, 5000); 
    // Set src after setting up event handlers
    img.src = imagePath;
    return () => clearTimeout(timeoutId);
  };
  useEffect(() => {
    if(!isOpen)
    {
      setCurrentPage(1);
      setLoadingStates({});
      setActiveSection('Introduction');
      setPreloadedImages({});
      setPreloadedVideos({});
      setVideoLoadingStates({});
    }
  },[!isOpen])
  useEffect(() => {
    if (!isOpen || !currentSectionData?.pages) return;
    // Load current page media (image or video)
    const currentPageContent = currentSectionData.pages[currentPage - 1];
    if (currentPageContent) {
        const key = `${activeSection}-${currentPage}`;
        // Check if it's a video page AND it has a videoPath AND it hasn't already been preloaded
        if (currentPageContent.videoPath && !preloadedVideos[key]) {
            setPreloadedVideos(prev => ({
                ...prev,
                [key]: currentPageContent.videoPath!
            }));

            // Optionally, you could check if the video exists
            const videoElement = document.createElement('video');
            videoElement.src = currentPageContent.videoPath || ''; // Use currentPageContent.videoPath, it's now guaranteed to exist
            videoElement.preload = "auto";
            videoElement.load();

            const handleCanPlay = () => {
                setVideoLoadingStates(prev => ({
                    ...prev,
                    [key]: false
                }));
            };

            const handleError = (e: Event | string) => {
                const target = e instanceof Event ? e.target as HTMLVideoElement : null;
                if (!target || target.networkState === 3) {
                    console.warn(`Video error likely benign for ${key}`);
                } else {
                    console.error(`Error loading video for ${key}`);
                }

                setVideoLoadingStates(prev => ({
                    ...prev,
                    [key]: false
                }));
            };

            videoElement.addEventListener('canplay', handleCanPlay);
            videoElement.addEventListener('error', handleError);
            
            setVideoLoadingStates(prev => ({
                ...prev,
                [key]: true
            }));

            // Return cleanup function
            return () => {
                videoElement.removeEventListener('canplay', handleCanPlay);
                videoElement.removeEventListener('error', handleError);
                videoElement.src = '';
                videoElement.remove();
            };
        }

        // If it's an image page and needs loading
        else if (!preloadedImages[key] && currentPageContent.imagePath) {
            const cleanup = tryLoadImage(
                currentPageContent.imagePath,
                currentPageContent.backupImagePath || "",
                key
            );
            return cleanup;
        }
    }
}, [activeSection, currentPage, isOpen, currentSectionData, preloadedVideos]);
  
  // Similarly for the second useEffect
  useEffect(() => {
    if (!isOpen || !currentSectionData?.pages || currentPage >= totalPages) return; 
    const currentKey = `${activeSection}-${currentPage}`;
    const currentPageContent = currentSectionData.pages[currentPage - 1];
    const currentPageReady = 
      (currentPageContent.isVideo) || 
      (preloadedImages[currentKey] && !loadingStates[currentKey]);
    
    if (currentPageReady) {
      const nextPage = currentPage + 1;
      if (nextPage <= totalPages) {
        const nextPageContent = currentSectionData.pages[nextPage - 1];
        const nextKey = `${activeSection}-${nextPage}`;
        
        // Only try to preload image if it's not a video and has an imagePath
        if (!nextPageContent.isVideo && 
            !preloadedImages[nextKey] && 
            !loadingStates[nextKey] && 
            nextPageContent.imagePath) {
          const cleanup = tryLoadImage(
            nextPageContent.imagePath,
            nextPageContent.backupImagePath || "",
            nextKey
          );
          return cleanup;
        }
      }
    }
  }, [preloadedImages, loadingStates, videoLoadingStates, preloadedVideos, activeSection, currentPage, totalPages, isOpen, currentSectionData]);
 
  // Handle navigation - completely independent from image loading
  const handlePrevClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleSectionClick = (section: string) => {
    setActiveSection(section);
    setCurrentPage(1);
  };

  if (!isOpen) return null;
  // Get current page content
  const currentPageContent = totalPages > 0 && currentPage <= totalPages 
    ? currentSectionData.pages[currentPage - 1] 
    : null;
    
  // Determine which image to show
  const getImageToShow = () => {
    if (!currentPageContent) return null;
    
    const imageKey = `${activeSection}-${currentPage}`;
    const isLoading = loadingStates[imageKey];
    const imageSrc = preloadedImages[imageKey] || currentPageContent.imagePath;
    
    return (
      <div className="section-content-image" style={{ position: "relative", width: "100%", height: "100%",  maxHeight: "75vh", overflow: "hidden" }}>
        {preloadedImages[imageKey] ? (
          <img
            src={imageSrc}
            id={currentPageContent.id}
            alt={`${currentPageContent.title} Slide`}
            className="section-image"
            style={{ 
              maxWidth: "100%", 
              height: "auto", 
              display: "block",
              objectFit: "contain",
              maxHeight: "72vh",
              margin: "0 auto",
              boxSizing: "border-box"
            }}
          />
        ) : (
          <div style={{
            width: "100%",
            backgroundColor: "#f5f5f5", 
            padding: "20px",
            textAlign: "center",
            borderRadius: "5px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "300px"
          }}>
             <div>Loading image...</div>
               <div style={{ marginTop: "10px", fontSize: "14px", color: "#777" }}>
                 You can continue to navigate while images load
              </div>
          </div>
        )}
      </div>
    );
  };
  return (
    <div
      className="ui-dialog ui-widget ui-widget-content ui-corner-all left-align-text tutorial"
       style={{
        width: "94vw",
        height: "88vh",
        minWidth: 1400,
        maxWidth: 1800,
        maxHeight: "95vh",
        overflow: "hidden",
        zIndex: 100,
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        display: isOpen ? "block" : "none",
        padding: "4px",
        borderRadius: "8px",   
      }}
    >
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <div className="ui-dialog-titlebar ui-corner-all ui-widget-header ui-helper-clearfix">
        <span id="ui-id-2" className="ui-dialog-title">Foundations™ by HealthLevel</span>
        <button
          type="button"
          className="ui-button ui-corner-all ui-widget ui-button-icon-only ui-dialog-titlebar-close"
          title="Close"
          onClick={onClose}
        >
          <span className="ui-button-icon ui-icon ui-icon-closethick"></span>
          <span className="ui-button-icon-space"> </span>
        </button>
      </div>
      
      {/* Tutorial Sections Navigation */}
      <div
        id="tutorial_dialog_wrapper"
        className="ui-dialog-content ui-widget-content"
        style={{ display: "block", width: "auto", minHeight: "0px", maxHeight: "none", height: "auto" }}
      >
        <div className="sections">
          <ul id="tutorial_section_list" className="section-list">
            <li
              id="tutorial_section_list_item_1"
              className={`section-list-item ${activeSection === 'Introduction' ? 'section-list-item-active' : ''}`}
              onClick={() => handleSectionClick('Introduction')}
            >
              Introduction
            </li>
            <li
              id="tutorial_section_list_item_2"
              className={`section-list-item ${activeSection === 'Homescreen' ? 'section-list-item-active' : ''}`}
              onClick={() => handleSectionClick('Homescreen')}
            >
              Homescreen
            </li>
            <li
              id="tutorial_section_list_item_3"
              className={`section-list-item ${activeSection === 'Workspaces' ? 'section-list-item-active' : ''}`}
              onClick={() => handleSectionClick('Workspaces')}
            >
              Workspaces
            </li>
            <li
              id="tutorial_section_list_item_4"
              className={`section-list-item ${activeSection === 'Reports' ? 'section-list-item-active' : ''}`}
              onClick={() => handleSectionClick('Reports')}
            >
              Reports
            </li>
            <li
              id="tutorial_section_list_item_5"
              className={`section-list-item ${activeSection === 'User Management' ? 'section-list-item-active' : ''}`}
              onClick={() => handleSectionClick('User Management')}
            >
              User Management
            </li>
            <li
              id="tutorial_section_list_item_6"
              className={`section-list-item ${activeSection === 'Advanced Topics' ? 'section-list-item-active' : ''}`}
              onClick={() => handleSectionClick('Advanced Topics')}
            >
              Advanced Topics
            </li>
            <li
              id="tutorial_section_list_item_7"
              className={`section-list-item ${activeSection === 'Training Videos' ? 'section-list-item-active' : ''}`}
              onClick={() => handleSectionClick('Training Videos')}
            >
              Training Videos
            </li>
          </ul>
        </div>
      </div>
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100% - 100px)" }}>
            <div style={{ display: "flex", alignItems: "stretch", gap: "4px", height: "100%" }}>      
                {/* Image OR Video Section */}
                <div style={{ flex: "2.2", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {/* Show Image if NOT Video */}
                    {!currentPageContent?.isVideo && getImageToShow()}
                    {/* Show Video if `isVideo` is true */}
                    {currentPageContent?.isVideo && currentPageContent?.videoPath && (
                    <div className="section-content-image" style={{ position: "relative", width: "100%", height: "72vh" }}>
                    <video
                      key={currentPageContent.videoPath}
                      id={currentPageContent.id}
                      controls
                      autoPlay
                      className="section-image"
                      style={{ 
                        maxWidth: "100%",
                        height: "100%",
                        display: "block",
                        margin: "0 auto"
                      }}
                    >
                      <source src={currentPageContent.videoPath} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                    )}
                </div>
          {/* Notes Section with fixed height to ensure navigation stays visible */}
          <div 
            className="section-content-note-wrapper" 
            style={{ 
              flex: "0.8", 
              display: "flex", 
              flexDirection: "column",
              maxHeight: "60vh"
            }}
          >
            {/* Content area with scrolling */}
            <div style={{ overflowY: "auto", flexGrow: 1, marginBottom: "10px" }}>
              {currentPageContent && (
                <div>
                  <div className="section-note-header">{currentPageContent.title}</div>
                  <div
                    className="section-note-data"
                    dangerouslySetInnerHTML={{ __html: currentPageContent.content }}
                  />
                </div>
              )}
            </div>
            {/* Navigation Buttons - always visible at bottom */}
            <div 
              className="section-content-buttons"
              style={{ 
                textAlign: "right", 
                marginTop: "10px",
                padding: "10px 0",
                borderTop: "1px solid #eee"
              }}
            >
              <span id="tutorial_page_info">{currentPage} of {totalPages}</span>
              <span className="padding-left-small" style={{ marginLeft: "10px" }}></span>
              <button
                className={`app-button ${currentPage === 1 ? "disabled" : ""}`}
                onClick={handlePrevClick}
                disabled={currentPage === 1}
                title="Previous slide"
                style={{ marginRight: "5px" }}
              >
                ◄
              </button>
              <button
                className={`app-button ${currentPage === totalPages ? "disabled" : ""}`}
                onClick={handleNextClick}
                disabled={currentPage === totalPages}
                title="Next slide"
              >
                ►
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoundationsGuide;